import { useEffect, useState } from "react";
import { GripVertical, ArrowUp, ArrowDown, Maximize2, Minimize2, RotateCcw } from "lucide-react";

// Patch 55 Part I/J — resizable, reorderable dashboard widgets persisted PER USER (storageKey carries the
// user id). Drag-and-drop for pointer devices PLUS keyboard-accessible move-up/down + grow/shrink buttons
// (drag alone is not accessible to keyboard or reliable on touch). Reordering/resizing never removes a
// widget or changes its data; "Reset layout" restores the platform default. Rule-of-3: only `visibleCount`
// widgets show by default, the rest sit behind a real toggle.
export default function DashboardWidgets({ widgets, storageKey, visibleCount = 3 }) {
  const ids = widgets.map(w => w.id);
  const [layout, setLayout] = useState({ order: ids, sizes: {} });
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (saved && Array.isArray(saved.order)) {
        const order = saved.order.filter(id => ids.includes(id)).concat(ids.filter(id => !saved.order.includes(id)));
        setLayout({ order, sizes: saved.sizes || {} });
      } else setLayout({ order: ids, sizes: {} });
    } catch { setLayout({ order: ids, sizes: {} }); }
    // eslint-disable-next-line
  }, [storageKey, ids.join(",")]);

  const persist = (next) => { setLayout(next); try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ } };
  const move = (id, dir) => {
    const o = [...layout.order]; const i = o.indexOf(id); const j = i + dir;
    if (j < 0 || j >= o.length) return; [o[i], o[j]] = [o[j], o[i]]; persist({ ...layout, order: o });
  };
  const resize = (id) => {
    const next = (layout.sizes[id] || "full") === "full" ? "half" : "full";
    persist({ ...layout, sizes: { ...layout.sizes, [id]: next } });
  };
  const reset = () => persist({ order: ids, sizes: {} });
  const onDrop = (target) => {
    if (!drag || drag === target) return;
    const o = [...layout.order]; const from = o.indexOf(drag); const to = o.indexOf(target);
    o.splice(to, 0, o.splice(from, 1)[0]); persist({ ...layout, order: o }); setDrag(null);
  };

  const byId = Object.fromEntries(widgets.map(w => [w.id, w]));
  const ordered = layout.order.map(id => byId[id]).filter(w => w && w.node);

  const renderWidget = (w, idx) => (
    <div key={w.id} data-testid={`widget-${w.id}`} draggable
      onDragStart={() => setDrag(w.id)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(w.id)}
      className="k-container" style={{ padding: 0, marginBottom: 16,
        maxWidth: layout.sizes[w.id] === "half" ? 560 : "100%", opacity: drag === w.id ? 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
        <GripVertical size={15} className="k-muted" aria-hidden="true" style={{ cursor: "grab" }} />
        <span className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{w.label}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          <button className="k-btn k-btn-ghost" data-testid={`widget-up-${w.id}`} aria-label={`Move ${w.label} up`}
            disabled={idx === 0} style={{ padding: 6, minHeight: 0 }} onClick={() => move(w.id, -1)}><ArrowUp size={14} /></button>
          <button className="k-btn k-btn-ghost" data-testid={`widget-down-${w.id}`} aria-label={`Move ${w.label} down`}
            disabled={idx === ordered.length - 1} style={{ padding: 6, minHeight: 0 }} onClick={() => move(w.id, 1)}><ArrowDown size={14} /></button>
          <button className="k-btn k-btn-ghost" data-testid={`widget-size-${w.id}`}
            aria-label={`${layout.sizes[w.id] === "half" ? "Make larger" : "Make smaller"}: ${w.label}`}
            style={{ padding: 6, minHeight: 0 }} onClick={() => resize(w.id)}>
            {layout.sizes[w.id] === "half" ? <Maximize2 size={14} /> : <Minimize2 size={14} />}</button>
        </div>
      </div>
      <div style={{ padding: 16 }}>{w.node}</div>
    </div>
  );

  const prominent = ordered.slice(0, visibleCount);
  const rest = ordered.slice(visibleCount);
  return (
    <div data-testid="dashboard-widgets">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="k-btn k-btn-ghost" data-testid="widgets-reset" style={{ fontSize: 12, padding: "6px 12px" }}
          onClick={reset}><RotateCcw size={13} /> Reset layout</button>
      </div>
      {prominent.map((w, i) => renderWidget(w, i))}
      {rest.length > 0 && (
        <details data-testid="widgets-more">
          <summary className="k-link" data-testid="widgets-more-toggle" style={{ cursor: "pointer", fontSize: 13, marginBottom: 12 }}>
            Show {rest.length} more widget{rest.length > 1 ? "s" : ""}</summary>
          <div>{rest.map((w, i) => renderWidget(w, i + visibleCount))}</div>
        </details>
      )}
    </div>
  );
}
